/**
 * Formata um valor numérico para moeda BRL.
 * @param {number} value - O valor a ser formatado.
 * @returns {string} Valor formatado em BRL.
 */
export const formatCurrency = (value = 0) => {
  return value.toLocaleString("pt-br", { style: "currency", currency: "BRL" });
};

/**
 * Alias para formatCurrency (compatibilidade)
 * @deprecated Use formatCurrency ao invés de formatBRL
 */
export const formatBRL = formatCurrency;

/**
 * Formata um número de telefone brasileiro
 * @param {string|number} phone - Número de telefone
 * @param {boolean} includeMask - Se deve incluir máscara (XX) XXXXX-XXXX
 * @returns {string} Telefone formatado
 */
export const formatPhone = (phone, includeMask = true) => {
  if (!phone) return "";
  
  const cleanPhone = String(phone).replace(/\D/g, "");
  
  if (cleanPhone.length === 10) {
    // Telefone fixo: (XX) XXXX-XXXX
    if (includeMask) {
      return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, 6)}-${cleanPhone.slice(6)}`;
    }
    return cleanPhone;
  } else if (cleanPhone.length === 11) {
    // Celular: (XX) 9XXXX-XXXX
    if (includeMask) {
      return `(${cleanPhone.slice(0, 2)}) ${cleanPhone.slice(2, 7)}-${cleanPhone.slice(7)}`;
    }
    return cleanPhone;
  }
  
  return phone; // Retorna original se não conseguir formatar
};

/**
 * Formata um CEP brasileiro
 * @param {string|number} zipCode - CEP
 * @param {boolean} includeMask - Se deve incluir máscara (XXXXX-XXX)
 * @returns {string} CEP formatado
 */
export const formatZipCode = (zipCode, includeMask = true) => {
  if (!zipCode) return "";
  
  const cleanZipCode = String(zipCode).replace(/\D/g, "");
  
  if (cleanZipCode.length === 8 && includeMask) {
    return `${cleanZipCode.slice(0, 5)}-${cleanZipCode.slice(5)}`;
  }
  
  return cleanZipCode || zipCode;
};

/**
 * Formata uma data
 * @param {Date|string} date - Data a formatar
 * @param {string} format - Formato desejado ('dd/MM/yyyy', 'dd/MM/yyyy HH:mm', etc.)
 * @returns {string} Data formatada
 */
export const formatDate = (date, format = "dd/MM/yyyy") => {
  if (!date) return "";
  
  const dateObj = date instanceof Date ? date : new Date(date);
  
  if (isNaN(dateObj.getTime())) {
    return "";
  }
  
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  const hours = String(dateObj.getHours()).padStart(2, "0");
  const minutes = String(dateObj.getMinutes()).padStart(2, "0");
  
  return format
    .replace("dd", day)
    .replace("MM", month)
    .replace("yyyy", year)
    .replace("HH", hours)
    .replace("mm", minutes);
};

/**
 * Formata CPF ou CNPJ
 * @param {string|number} document - CPF ou CNPJ
 * @returns {string} Documento formatado
 */
export const formatDocument = (document) => {
  if (!document) return "";
  
  const cleanDoc = String(document).replace(/\D/g, "");
  
  if (cleanDoc.length === 11) {
    // CPF: XXX.XXX.XXX-XX
    return `${cleanDoc.slice(0, 3)}.${cleanDoc.slice(3, 6)}.${cleanDoc.slice(6, 9)}-${cleanDoc.slice(9)}`;
  } else if (cleanDoc.length === 14) {
    // CNPJ: XX.XXX.XXX/XXXX-XX
    return `${cleanDoc.slice(0, 2)}.${cleanDoc.slice(2, 5)}.${cleanDoc.slice(5, 8)}/${cleanDoc.slice(8, 12)}-${cleanDoc.slice(12)}`;
  }
  
  return document;
};

/**
 * Formata um número removendo zeros à esquerda
 * @param {string|number} value - Valor a formatar
 * @returns {string} Número formatado
 */
export const formatNumber = (value) => {
  if (value === null || value === undefined) return "";
  return String(value).replace(/^0+/, "") || "0";
};

/**
 * Formata texto para título (primeira letra maiúscula)
 * @param {string} text - Texto a formatar
 * @returns {string} Texto formatado
 */
export const formatTitle = (text) => {
  if (!text) return "";
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

/**
 * Formata texto para nome próprio (cada palavra com primeira letra maiúscula)
 * @param {string} text - Texto a formatar
 * @returns {string} Texto formatado
 */
export const formatProperName = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

